import React from 'react';
import { Avatar, Box, Center, Flex, Text, useColorModeValue, useDisclosure, VStack } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { SEHomeHeaderIcon } from 'clipsal-cortex-icons/src/custom-icons';
import { AlertDialogModal } from 'clipsal-cortex-ui/src/components/AlertDialogModal';

import packageJson from '../../../package.json';
import { SEHomeCard } from '../../common/components/SEHomeCard';
import { SlidingAnimationPageBase } from '../../common/components/SlidingAnimationPageBase';
import { IS_DEMO_LOGIN } from '../../common/constants';
import { useSiteCapabilityCheck } from '../../common/hooks/use-site-capability-check';
import { SEHomeNavHomeIcon } from '../../styles/custom-icons';
import { FeatureFlag, getFlagValue } from '../../utils/common/feature-flag-helpers';
import { SimulationDeveloperTool } from '../ev-charger/dashboard/SimulationDeveloperTool';
import { selectSite } from '../site/siteSlice';
import { selectUser } from '../user/userSlice';
import { AccountMenuButton } from './AccountMenuButton';
import { TermsOfUseModal } from './TermsOfUseModal';

export function AccountDashboard() {
  const navigate = useNavigate();
  const user = useSelector(selectUser);
  const { site_id: siteId } = useSelector(selectSite);
  const site = useSelector(selectSite);
  const { siteHasInverter, siteHasBattery, siteHasInverterSolar } = useSiteCapabilityCheck();
  const { isOpen: isLogoutOpen, onOpen: onLogoutOpen, onClose: onLogoutClose } = useDisclosure();
  const { isOpen: isTermsOpen, onOpen: onTermsOpen, onClose: onTermsClose } = useDisclosure();
  const { t } = useTranslation();
  const iconColor = useColorModeValue('customGrey.900', 'customGrey.400');

  return (
    <SlidingAnimationPageBase
      backURL={`../../home?direction=back`}
      title={<SEHomeHeaderIcon w={'100%'} color={iconColor} />}
    >
      <Box px={4}>
        <Box>
          <SEHomeCard mb={5} borderTopLeftRadius={'5px'} p={0}>
            <AccountMenuButton
              py={10}
              includeBottomDivider={false}
              data-testid={'personal-details'}
              onClick={() => navigate('../personal-details')}
            >
              <Flex>
                <Avatar size={'sm'} mr={2} name={`${user.firstName} ${user.lastName}`} />
                <Flex align="flex-start" flexDirection="column" whiteSpace="pre-wrap">
                  <Text fontSize={16} fontWeight="normal">
                    {user.firstName} {user.lastName}
                  </Text>
                  <Text color="#979797" fontSize={14} fontWeight="normal" overflowWrap="anywhere" textAlign="left">
                    {user.email}
                  </Text>
                </Flex>
              </Flex>
            </AccountMenuButton>
          </SEHomeCard>

          <SEHomeCard mb={5} borderTopLeftRadius={'5px'} p={0}>
            {siteHasInverter && (
              <>
                <AccountMenuButton data-testid="energy-modes" onClick={() => navigate(`../energy-modes/home`)}>
                  <Text fontSize={16} fontWeight="normal">
                    {t('Energy Modes.energy modes')}
                  </Text>
                </AccountMenuButton>
                {/* VPP is limited to Staging until Saturn VPP APIs are available in Prod */}
                {siteHasBattery && siteHasInverterSolar && getFlagValue(FeatureFlag.EnableVPP) && (
                  <AccountMenuButton data-testid="vpp-programs" onClick={() => navigate(`../vpp/home`)}>
                    <Text fontSize={16} fontWeight="normal">
                      {t('VPP.nav title')}
                    </Text>
                  </AccountMenuButton>
                )}
              </>
            )}

            <AccountMenuButton data-testid="hardware" onClick={() => navigate(`../hardware`)}>
              <Text fontSize={16} fontWeight="normal">
                {t('Hardware.hardware')}
              </Text>
            </AccountMenuButton>

            <AccountMenuButton data-testid="settings" onClick={() => navigate('../settings/home')}>
              <Text fontSize={16} fontWeight="normal">
                {t('Account.settings')}
              </Text>
            </AccountMenuButton>

            <AccountMenuButton data-testid="tariff" onClick={() => navigate('../tariff')} includeBottomDivider={false}>
              <Text fontSize={16} fontWeight="normal">
                {t('Energy Rates.energy rates')}
              </Text>
            </AccountMenuButton>
          </SEHomeCard>
        </Box>
        <SEHomeCard mb={5} borderTopLeftRadius={'5px'} p={0}>
          <AccountMenuButton data-testid={'support'} onClick={() => navigate('../support')}>
            <Text fontSize={16} fontWeight="normal">
              {t('Support.support')}
            </Text>
          </AccountMenuButton>

          <AccountMenuButton data-testid={'terms-of-use-button'} onClick={onTermsOpen}>
            <Text fontSize={16} fontWeight="normal">
              {t('Common.terms of use')}
            </Text>
          </AccountMenuButton>

          <AccountMenuButton
            data-testid={'privacy-settings-button'}
            includeBottomDivider={false}
            onClick={() =>
              navigate(
                `../settings/privacy?direction=forward&backURL=/sites/${siteId}/account/dashboard?direction=back`
              )
            }
          >
            <Text fontSize={16} fontWeight="normal">
              {t('Common.privacy')}
            </Text>
          </AccountMenuButton>
        </SEHomeCard>

        <SEHomeCard mb={5} borderTopLeftRadius={'5px'} p={0}>
          <AccountMenuButton
            data-testid="manage-homes"
            onClick={() => navigate(`/sites/list?backURL=/sites/${siteId}/account/dashboard?direction=back`)}
          >
            <Flex data-testid="home-name" align="center" width="90%">
              <SEHomeNavHomeIcon mr={2} w={6} h={6} />
              <VStack align={'flex-start'} rowGap={0}>
                <Box fontWeight={500} fontSize={18}>
                  {site.site_name}
                </Box>
                <Box fontWeight={400} fontSize={12} textTransform={'uppercase'}>
                  {t('Common.site id')}: {site.site_id}
                </Box>
              </VStack>
            </Flex>
          </AccountMenuButton>

          <AccountMenuButton
            isDisabled={IS_DEMO_LOGIN}
            data-testid="logout"
            onClick={onLogoutOpen}
            includeChevron={false}
            includeBottomDivider={false}
          >
            <Text fontSize={16} fontWeight="normal">
              {t('Account.logout')}
            </Text>
          </AccountMenuButton>
        </SEHomeCard>

        <Center data-testid="app-version" mt={4}>
          <Text>
            {t('Account.app version')}: {packageJson.version}
          </Text>
        </Center>

        <SEHomeCard mt={5} borderTopLeftRadius={'5px'} p={0}>
          <SimulationDeveloperTool />
        </SEHomeCard>

        <AlertDialogModal
          isOpen={isLogoutOpen}
          onClose={onLogoutClose}
          header={t('Account.logout')}
          subHeader={t('Account.are you sure logout')}
          confirmButtonName={t('Account.logout')}
          onConfirm={() => navigate('/logout')}
          cancelButtonName={t('Common.cancel')}
        />

        <TermsOfUseModal isOpen={isTermsOpen} onClose={onTermsClose} />
      </Box>
    </SlidingAnimationPageBase>
  );
}
